import { Injectable, inject, signal, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { io, Socket } from 'socket.io-client';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { selectAuthToken } from '@store/auth/auth.selectors';

export interface AgentMessage {
  role: 'user' | 'assistant' | 'tool';
  content: string;
  tool?: string;
  timestamp: Date;
}

export type AgentStatus = 'idle' | 'thinking' | 'error';

@Injectable({ providedIn: 'root' })
export class ErpAgentService {
  private store   = inject(Store);
  private zone    = inject(NgZone);
  private socket: Socket | null = null;
  private pendingMessage: string | null = null;

  private readonly wsBase = environment.apiUrl.replace(/\/api$/, '');

  readonly messages    = signal<AgentMessage[]>([]);
  readonly status      = signal<AgentStatus>('idle');
  readonly connected   = signal(false);
  readonly socketErr   = signal<string | null>(null);
  readonly currentTool = signal<string | null>(null);

  async connectSocket(): Promise<void> {
    if (this.socket?.connected) return;

    const token = await firstValueFrom(this.store.select(selectAuthToken));
    if (!token) return;

    this.socket?.disconnect();
    this.socket = null;

    this.socket = this.zone.runOutsideAngular(() =>
      io(`${this.wsBase}/erp-agent`, { auth: { token }, reconnection: true })
    );

    this.socket.on('connect', () => this.zone.run(() => {
      this.connected.set(true);
      this.socketErr.set(null);
      // Enviar mensaje que llegó antes de que el socket conectara
      if (this.pendingMessage && this.socket) {
        this.socket.emit('agent:message', { text: this.pendingMessage });
        this.pendingMessage = null;
      }
    }));

    this.socket.on('connect_error', (err) => this.zone.run(() => {
      this.socketErr.set(err.message);
      this.connected.set(false);
      // Liberar mensaje pendiente si falla la conexión
      if (this.pendingMessage) {
        this.status.set('idle');
        this.messages.update(msgs => [...msgs, {
          role: 'assistant',
          content: `⚠️ No se pudo conectar al servidor: ${err.message}`,
          timestamp: new Date(),
        }]);
        this.pendingMessage = null;
      }
    }));

    this.socket.on('disconnect', () => this.zone.run(() => {
      this.connected.set(false);
    }));

    this.socket.on('agent:history', (history: { role: 'user' | 'assistant'; content: string }[]) => {
      this.zone.run(() => {
        if (history.length > 0) {
          this.messages.set(history.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: new Date(),
          })));
        }
      });
    });

    this.socket.on('agent:thinking', (ev: { tool: string }) => {
      this.zone.run(() => {
        this.status.set('thinking');
        this.currentTool.set(ev.tool);
      });
    });

    this.socket.on('agent:tool-result', (ev: { tool: string; result: unknown }) => {
      this.zone.run(() => {
        this.messages.update(msgs => [...msgs, {
          role: 'tool',
          content: JSON.stringify(ev.result, null, 2),
          tool: ev.tool,
          timestamp: new Date(),
        }]);
      });
    });

    this.socket.on('agent:reply', (ev: { text: string }) => {
      this.zone.run(() => {
        this.status.set('idle');
        this.currentTool.set(null);
        this.messages.update(msgs => [...msgs, {
          role: 'assistant',
          content: ev.text,
          timestamp: new Date(),
        }]);
      });
    });

    this.socket.on('agent:error', (ev: { message: string }) => {
      this.zone.run(() => {
        this.status.set('idle');
        this.currentTool.set(null);
        this.messages.update(msgs => [...msgs, {
          role: 'assistant',
          content: `⚠️ ${ev.message}`,
          timestamp: new Date(),
        }]);
      });
    });

    this.socket.on('agent:cleared', () => {
      this.zone.run(() => this.messages.set([]));
    });
  }

  disconnectSocket(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.connected.set(false);
  }

  sendMessage(text: string): void {
    const msg = text.trim();
    if (!msg) return;

    // Agregar mensaje del usuario a la lista de inmediato
    this.messages.update(msgs => [...msgs, {
      role: 'user',
      content: msg,
      timestamp: new Date(),
    }]);
    this.status.set('thinking');

    if (this.socket?.connected) {
      this.socket.emit('agent:message', { text: msg });
    } else {
      // Encolar: se enviará automáticamente cuando el socket conecte
      this.pendingMessage = msg;
    }
  }

  clearHistory(): void {
    this.socket?.emit('agent:clear');
  }
}
